import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Building2, Calendar, Users, Plus, Trash2,
  RefreshCw, Check, AlertCircle, ExternalLink, X, Rss, Home
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCommunity } from '../contexts/CommunityContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

const CARE_LEVELS = [
  'Independent Living',
  'Assisted Living',
  'Memory Care',
  'Skilled Nursing',
  'Adult Day Care',
  'Group Home',
  'Home Health',
  'Respite',
  'Hospice',
  'Other'
];

export default function CommunitySettingsPage() {
  const navigate = useNavigate();
  const { communityId } = useParams();
  const { user } = useAuth();
  const { community, staff, isAdmin, inviteStaff, removeStaff, loadDashboard } = useCommunity();
  const functions = getFunctions();

  const [activeTab, setActiveTab] = useState('general');

  // General settings state
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [careLevels, setCareLevels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Calendar feeds state
  const [feeds, setFeeds] = useState([]);
  const [feedsLoading, setFeedsLoading] = useState(false);
  const [showAddFeed, setShowAddFeed] = useState(false);
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [newFeedCareLevel, setNewFeedCareLevel] = useState('');
  const [newFeedName, setNewFeedName] = useState('');
  const [addingFeed, setAddingFeed] = useState(false);
  const [feedError, setFeedError] = useState(null);
  const [syncingFeedId, setSyncingFeedId] = useState(null);

  // Staff state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('staff');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Initialize form from community data
  useEffect(() => {
    if (community) {
      setName(community.name || '');
      setAddress(community.address || '');
      setPhone(community.phone || '');
      setCareLevels(community.careLevels || []);
    }
  }, [community]);

  // Load calendar feeds
  useEffect(() => {
    if (communityId && activeTab === 'calendar') {
      loadFeeds();
    }
  }, [communityId, activeTab]);

  const loadFeeds = async () => {
    setFeedsLoading(true);
    try {
      const loadCalendarFeeds = httpsCallable(functions, 'loadCalendarFeeds');
      const result = await loadCalendarFeeds({ communityId });
      if (result.data.success) {
        setFeeds(result.data.feeds);
      }
    } catch (err) {
      console.error('Error loading feeds:', err);
    } finally {
      setFeedsLoading(false);
    }
  };

  // ============================================
  // General settings handlers
  // ============================================

  const toggleCareLevel = (level) => {
    setCareLevels(prev =>
      prev.includes(level)
        ? prev.filter(l => l !== level)
        : [...prev, level]
    );
  };

  const handleSaveGeneral = async () => {
    if (!name.trim()) {
      setSaveError('Community name is required');
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      const updateCommunity = httpsCallable(functions, 'updateCommunity');
      await updateCommunity({
        communityId,
        name: name.trim(),
        address: address.trim(),
        phone: phone.trim(),
        careLevels
      });

      setSaveSuccess('Settings saved successfully');
      loadDashboard(); // Refresh community context
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      setSaveError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // Calendar feed handlers
  // ============================================

  const handleAddFeed = async () => {
    if (!newFeedUrl.trim()) {
      setFeedError('URL is required');
      return;
    }

    setAddingFeed(true);
    setFeedError(null);

    try {
      const addCalendarFeed = httpsCallable(functions, 'addCalendarFeed');
      const result = await addCalendarFeed({
        communityId,
        url: newFeedUrl.trim(),
        careLevel: newFeedCareLevel || 'All',
        name: newFeedName.trim() || newFeedCareLevel || 'Activities'
      });

      // Add to local state
      if (result.data.success) {
        setFeeds(prev => [result.data.feed, ...prev]);
        setShowAddFeed(false);
        setNewFeedUrl('');
        setNewFeedCareLevel('');
        setNewFeedName('');

        // Auto-sync the new feed
        handleSyncFeed(result.data.feedId);
      }
    } catch (err) {
      setFeedError(err.message || 'Failed to add feed');
    } finally {
      setAddingFeed(false);
    }
  };

  const handleRemoveFeed = async (feedId) => {
    try {
      const removeCalendarFeed = httpsCallable(functions, 'removeCalendarFeed');
      await removeCalendarFeed({ communityId, feedId });
      setFeeds(prev => prev.filter(f => f.id !== feedId));
    } catch (err) {
      console.error('Error removing feed:', err);
      alert('Failed to remove feed');
    }
  };

  const handleSyncFeed = async (feedId) => {
    setSyncingFeedId(feedId);
    try {
      const syncCalendarFeed = httpsCallable(functions, 'syncCalendarFeed', { timeout: 120000 });
      const result = await syncCalendarFeed({ communityId, feedId });

      if (result.data.success) {
        // Update local feed data
        setFeeds(prev => prev.map(f =>
          f.id === feedId
            ? {
                ...f,
                status: 'synced',
                eventCount: result.data.eventCount,
                calendarName: result.data.calendarName || f.name,
                lastSyncedAt: { _seconds: Date.now() / 1000 },
                lastError: null
              }
            : f
        ));
      }
    } catch (err) {
      console.error('Error syncing feed:', err);
      setFeeds(prev => prev.map(f =>
        f.id === feedId ? { ...f, status: 'error', lastError: err.message } : f
      ));
    } finally {
      setSyncingFeedId(null);
    }
  };

  // ============================================
  // Staff handlers
  // ============================================

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    setSendingInvite(true);
    setInviteError(null);

    try {
      await inviteStaff({
        email: inviteEmail.trim(),
        name: inviteName.trim() || null,
        role: inviteRole
      });
      setInviteSuccess(true);
    } catch (err) {
      setInviteError(err.message || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRemoveStaff = async (staffUid) => {
    setRemoving(true);
    try {
      await removeStaff(staffUid);
      setConfirmRemove(null);
    } catch (err) {
      alert(err.message || 'Failed to remove staff member');
    } finally {
      setRemoving(false);
    }
  };

  const resetInviteForm = () => {
    setShowInvite(false);
    setInviteEmail('');
    setInviteName('');
    setInviteRole('staff');
    setInviteError(null);
    setInviteSuccess(false);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'Never';
    const date = ts._seconds ? new Date(ts._seconds * 1000) : ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString();
  };

  const isSuperAdmin = user?.isSuperAdmin;
  const canEdit = isAdmin || isSuperAdmin;

  const tabs = [
    { id: 'general', label: 'General', icon: Building2 },
    { id: 'calendar', label: 'Calendar Feeds', icon: Calendar },
    { id: 'staff', label: 'Staff', icon: Users }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/community/${communityId}`)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-gray-900">Community Settings</h1>
              <p className="text-sm text-gray-500">{community?.name || 'Loading...'}</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Home"
            >
              <Home className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-quiltt-purple text-quiltt-purple'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">

        {/* ===== General Tab ===== */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            {saveSuccess && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
                <Check className="w-4 h-4" /> {saveSuccess}
              </div>
            )}
            {saveError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> {saveError}
              </div>
            )}

            {/* Community Info */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Community Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Community Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!canEdit}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    disabled={!canEdit}
                    placeholder="123 Main St, City, State ZIP"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={!canEdit}
                    placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple disabled:bg-gray-50"
                  />
                </div>
              </div>
            </div>

            {/* Care Levels */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Levels of Care</h2>
              <p className="text-sm text-gray-500 mb-4">
                Select the care levels your community offers. These are used to organize calendar feeds and activity recommendations.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {CARE_LEVELS.map(level => (
                  <button
                    key={level}
                    onClick={() => canEdit && toggleCareLevel(level)}
                    disabled={!canEdit}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-left transition-colors ${
                      careLevels.includes(level)
                        ? 'border-quiltt-purple bg-quiltt-50 text-quiltt-purple font-medium'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    } disabled:opacity-50`}
                  >
                    <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                      careLevels.includes(level)
                        ? 'bg-quiltt-purple border-quiltt-purple'
                        : 'border-gray-300'
                    }`}>
                      {careLevels.includes(level) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    {level}
                  </button>
                ))}
              </div>
            </div>

            {/* Save button */}
            {canEdit && (
              <div className="flex justify-end">
                <button
                  onClick={handleSaveGeneral}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-quiltt-purple text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ===== Calendar Feeds Tab ===== */}
        {activeTab === 'calendar' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Activity Calendar Feeds</h2>
                <p className="text-sm text-gray-500">
                  Add iCal feeds to import activity schedules. Events are used for personalized activity recommendations.
                </p>
              </div>
              {canEdit && (
                <button
                  onClick={() => setShowAddFeed(true)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-quiltt-purple text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Feed
                </button>
              )}
            </div>

            {/* Add feed form */}
            {showAddFeed && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">Add Calendar Feed</h3>
                  <button onClick={() => { setShowAddFeed(false); setFeedError(null); }} className="p-1 text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {feedError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {feedError}
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">iCal URL *</label>
                    <input
                      type="url"
                      value={newFeedUrl}
                      onChange={(e) => setNewFeedUrl(e.target.value)}
                      placeholder="https://example.com/calendar.ics"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple text-sm"
                    />
                    <p className="text-xs text-gray-400 mt-1">Paste the iCal/ICS feed URL from your calendar system</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Care Level</label>
                      <select
                        value={newFeedCareLevel}
                        onChange={(e) => setNewFeedCareLevel(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple text-sm"
                      >
                        <option value="">All Levels</option>
                        {(careLevels.length > 0 ? careLevels : CARE_LEVELS).map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Feed Name</label>
                      <input
                        type="text"
                        value={newFeedName}
                        onChange={(e) => setNewFeedName(e.target.value)}
                        placeholder="e.g. Memory Care Activities"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => { setShowAddFeed(false); setFeedError(null); }}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddFeed}
                      disabled={addingFeed}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-quiltt-purple text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {addingFeed ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Plus className="w-3.5 h-3.5" />
                          Add & Sync
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Feeds list */}
            {feedsLoading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-3 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-sm text-gray-500 mt-3">Loading feeds...</p>
              </div>
            ) : feeds.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <Rss className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="font-medium text-gray-900 mb-1">No calendar feeds yet</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Add an iCal feed URL to import your community's activity schedule.
                </p>
                {canEdit && !showAddFeed && (
                  <button
                    onClick={() => setShowAddFeed(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-quiltt-purple text-white text-sm font-medium rounded-lg hover:bg-purple-700"
                  >
                    <Plus className="w-4 h-4" />
                    Add Calendar Feed
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {feeds.map(feed => (
                  <div key={feed.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        feed.status === 'synced' ? 'bg-green-50' :
                        feed.status === 'error' ? 'bg-red-50' : 'bg-gray-100'
                      }`}>
                        <Calendar className={`w-5 h-5 ${
                          feed.status === 'synced' ? 'text-green-600' :
                          feed.status === 'error' ? 'text-red-500' : 'text-gray-400'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900 truncate">
                            {feed.calendarName || feed.name}
                          </h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            feed.status === 'synced' ? 'bg-green-100 text-green-700' :
                            feed.status === 'error' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {feed.status === 'synced' ? `${feed.eventCount} events` :
                             feed.status === 'error' ? 'Error' : 'Pending sync'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {feed.careLevel || 'All'} — Last synced: {formatTimestamp(feed.lastSyncedAt)}
                        </p>
                        {feed.lastError && (
                          <p className="text-xs text-red-500 mt-1">{feed.lastError}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1 truncate">{feed.url}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleSyncFeed(feed.id)}
                          disabled={syncingFeedId === feed.id}
                          className="p-2 text-gray-400 hover:text-quiltt-purple hover:bg-purple-50 rounded-lg disabled:opacity-50"
                          title="Sync now"
                        >
                          <RefreshCw className={`w-4 h-4 ${syncingFeedId === feed.id ? 'animate-spin' : ''}`} />
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => {
                              if (confirm(`Remove "${feed.calendarName || feed.name}" and all its events?`)) {
                                handleRemoveFeed(feed.id);
                              }
                            }}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                            title="Remove feed"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== Staff Tab ===== */}
        {activeTab === 'staff' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Staff Members</h2>
                <p className="text-sm text-gray-500">{staff?.length || 0} active staff members</p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowInvite(true)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-quiltt-purple text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Invite Staff
                </button>
              )}
            </div>

            {/* Invite modal */}
            {showInvite && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">Invite Staff Member</h3>
                  <button onClick={resetInviteForm} className="p-1 text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {inviteSuccess ? (
                  <div className="text-center py-4">
                    <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="font-medium text-gray-900">Invitation sent!</p>
                    <p className="text-sm text-gray-500 mt-1">They'll receive an email with a link to join.</p>
                    <button onClick={resetInviteForm} className="mt-3 text-sm text-quiltt-purple hover:underline">Done</button>
                  </div>
                ) : (
                  <>
                    {inviteError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{inviteError}</div>
                    )}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="staff@example.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                          <input
                            type="text"
                            value={inviteName}
                            onChange={(e) => setInviteName(e.target.value)}
                            placeholder="Optional"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                          <select
                            value={inviteRole}
                            onChange={(e) => setInviteRole(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple text-sm"
                          >
                            <option value="staff">Staff</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <button onClick={resetInviteForm} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
                        <button
                          onClick={handleInvite}
                          disabled={sendingInvite}
                          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-quiltt-purple text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                        >
                          {sendingInvite ? 'Sending...' : 'Send Invitation'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Staff list */}
            <div className="space-y-2">
              {staff?.map(member => (
                <div key={member.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-quiltt-50 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-quiltt-purple" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{member.name || member.email}</h3>
                    <p className="text-sm text-gray-500">{member.email}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    member.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {member.role === 'admin' ? 'Admin' : 'Staff'}
                  </span>
                  {isAdmin && member.id !== user?.uid && (
                    confirmRemove === member.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleRemoveStaff(member.id)}
                          disabled={removing}
                          className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                        >
                          {removing ? '...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmRemove(null)}
                          className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRemove(member.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
